import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Zap, Trophy, Target, TrendingUp, Wallet, Users, ChevronDown } from "lucide-react";
import { PublicNav, Footer } from "@/components/PublicChrome";
import { Embers } from "@/components/Embers";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const TIGER_IMG = "https://static.prod-images.emergentagent.com/jobs/0f0ac7ba-60f1-492a-a009-cff6f774132c/images/2f0ac9b7bfad710b89a1083722744edd4a01012711498789ded5bcc728478f3a.png";

const Counter = ({ value, prefix = "", suffix = "", label }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    const dur = 1600;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return (
    <div className="text-center" data-testid={`counter-${label.replace(/\s/g, "-").toLowerCase()}`}>
      <div className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-[#ff7a00] text-glow-orange">
        {prefix}{n.toLocaleString()}{suffix}
      </div>
      <div className="text-[#8a8a8a] text-xs sm:text-sm mt-2 tracking-[0.2em] uppercase">{label}</div>
    </div>
  );
};

export default function LandingPage() {
  const [stats, setStats] = useState({ total_traders: 0, funded_traders: 0, total_paid: 0, countries: 0 });
  const [challenges, setChallenges] = useState([]);
  const loc = useLocation();

  useEffect(() => {
    api.get("/stats/public").then((r) => setStats(r.data)).catch(() => {});
    api.get("/challenges").then((r) => setChallenges(r.data)).catch(() => {});
  }, []);

  // Smooth-scroll to in-page anchor when hash changes (e.g., /#why, /#process)
  useEffect(() => {
    if (!loc.hash) return;
    const id = loc.hash.slice(1);
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    // small delay to wait for content (challenges/stats) to render
    const t = setTimeout(tryScroll, 120);
    return () => clearTimeout(t);
  }, [loc.hash]);

  // Group challenges by type — Instant is hidden from the landing (Coming Soon)
  const grouped = {
    "1-Step": challenges.filter((c) => c.challenge_type === "1-Step"),
  };

  return (
    <div className="min-h-screen bg-[#050505] text-[#f5f5f5]">
      <PublicNav />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#ff7a00]/10">
        <Embers count={28} />
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="absolute -top-32 -left-32 w-[480px] h-[480px] bg-[#ff7a00]/15 rounded-full blur-[140px]" />
        <div className="absolute -bottom-40 -right-40 w-[560px] h-[560px] bg-[#ff9a2f]/10 rounded-full blur-[160px]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-16 pb-20 lg:pt-24 lg:pb-32">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="relative z-10"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#ff7a00]/30 bg-[#ff7a00]/5 text-[#ff9a2f] text-xs tracking-widest uppercase mb-6" data-testid="hero-badge">
                <span className="w-1.5 h-1.5 rounded-full bg-[#ff7a00] animate-pulse" /> We are back. Better. Stronger.
              </div>
              <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                Trade like a <span className="text-[#ff7a00] text-glow-orange">predator.</span><br/>
                Get funded like an <span className="text-[#ff9a2f]">institution.</span>
              </h1>
              <p className="mt-6 text-[#cfcfcf] text-base sm:text-lg max-w-xl leading-relaxed">
                The Leading Proprietary Trading Firm Empowering Synthetic Index Traders With Real Capital <span className="text-[#ff9a2f] font-semibold">Transparent Rules</span>, and <span className="text-[#ff9a2f] font-semibold">Lightning-Fast Payouts.</span>.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/challenges" data-testid="hero-cta-primary">
                  <Button size="lg" className="bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505] font-semibold px-7 h-12 glow-orange">
                    Start Challenge <ArrowRight size={18} className="ml-2" />
                  </Button>
                </Link>
                <a href="#process" data-testid="hero-cta-secondary">
                  <Button size="lg" variant="outline" className="border-[#ff7a00]/40 bg-transparent text-[#f5f5f5] hover:bg-[#ff7a00]/10 hover:text-[#ff9a2f] hover:border-[#ff7a00] h-12 px-7">
                    See How It Works
                  </Button>
                </a>
              </div>
              <div className="mt-10 flex items-center gap-6 text-[#8a8a8a] text-xs sm:text-sm">
                <div className="flex items-center gap-2"><ShieldCheck size={16} className="text-[#ff7a00]" /> Trusted</div>
                <div className="flex items-center gap-2"><Zap size={16} className="text-[#ff7a00]" /> 24/7 Markets</div>
                <div className="flex items-center gap-2"><Trophy size={16} className="text-[#ff7a00]" /> 80% Split</div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden border border-[#ff7a00]/30 glow-orange aspect-[3/4] lg:aspect-square max-w-md mx-auto neon-border">
                <img src={TIGER_IMG} alt="Tiger Warrior" className="w-full h-full object-cover" data-testid="hero-tiger-image" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 glass rounded-lg p-4">
                  <div className="text-[#8a8a8a] text-[10px] tracking-[0.3em] uppercase">Alpha Trader</div>
                  <div className="text-[#f5f5f5] font-display text-lg font-semibold mt-1">"Discipline is the only edge."</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* STATS COUNTERS */}
      <section className="border-b border-[#ff7a00]/10 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          <Counter value={stats.total_traders} suffix="+" label="Traders Worldwide" />
          <Counter value={stats.funded_traders} suffix="+" label="Funded Traders" />
          <Counter value={stats.total_paid} prefix="$" suffix="+" label="Paid in Payouts" />
          <Counter value={stats.countries} suffix="+" label="Countries" />
        </div>
      </section>

      {/* CHALLENGE PROGRAMS */}
      <section id="programs" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-2xl">
            <div className="text-[#ff7a00] text-xs tracking-[0.3em] uppercase mb-3">Challenge Programs</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">Pick your path. Get funded.</h2>
            <p className="text-[#8a8a8a] mt-4">Two routes to live capital. Each engineered for disciplined Synthetic Index traders.</p>
          </div>

          {Object.entries(grouped).map(([type, items]) => items.length > 0 && (
            <div key={type} className="mt-12">
              <div className="flex items-center gap-4 mb-6">
                <div className="text-[#f5f5f5] font-display text-xl sm:text-2xl font-semibold">{type} Evaluation</div>
                <div className="h-px flex-1 bg-gradient-to-r from-[#ff7a00]/40 to-transparent" />
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {items.map((c, i) => (
                  <motion.div
                    key={c.id}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.05 }}
                    className="glass rounded-xl p-6 hover:border-[#ff7a00]/50 hover:-translate-y-1 transition-all group"
                    data-testid={`challenge-card-${c.id}`}
                  >
                    <div className="text-[#8a8a8a] text-[10px] tracking-[0.25em] uppercase">{c.name}</div>
                    <div className="mt-3 font-display text-3xl font-bold text-[#f5f5f5] group-hover:text-[#ff7a00] transition">
                      ${c.account_size.toLocaleString()}
                    </div>
                    <div className="text-[#ff9a2f] text-sm mt-1">Account Size</div>
                    <div className="mt-5 space-y-2 text-sm text-[#cfcfcf]">
                      <div className="flex justify-between"><span className="text-[#8a8a8a]">Target</span><span>{c.profit_target_pct || "—"}%</span></div>
                      <div className="flex justify-between"><span className="text-[#8a8a8a]">Daily DD</span><span>{c.daily_drawdown_pct}%</span></div>
                      <div className="flex justify-between"><span className="text-[#8a8a8a]">Max DD</span><span>{c.max_drawdown_pct}%</span></div>
                      <div className="flex justify-between"><span className="text-[#8a8a8a]">Split</span><span className="text-[#ff9a2f]">{c.profit_split_pct}%</span></div>
                    </div>
                    <div className="mt-6 pt-5 border-t border-[#ff7a00]/15 flex items-end justify-between">
                      <div>
                        <div className="text-[#8a8a8a] text-[10px] uppercase tracking-wider">From</div>
                        <div className="font-display text-2xl font-bold text-[#f5f5f5]">${c.price_usd}</div>
                      </div>
                      <Link to="/challenges" data-testid={`buy-${c.id}`}>
                        <Button size="sm" className="bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505] font-semibold">
                          Buy
                        </Button>
                      </Link>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WHY US */}
      <section id="why" className="py-20 border-t border-[#ff7a00]/10 bg-[#080808]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-[#ff7a00] text-xs tracking-[0.3em] uppercase mb-3">Why Funded Index</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold max-w-2xl">Built for the few who refuse to lose.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {[
              { icon: Target, title: "Synthetic Index Focus", body: "Trade Volatility, Boom & Crash, Step indices 24/7 on the platforms you already use." },
              { icon: Wallet, title: "No Payout Limits", body: "Get paid what you earn, anytime, anywhere" },
              { icon: Trophy, title: "Up to 80% Split", body: "Keep what you earn. Scale your account every 4 months on consistent performance." },
              { icon: ShieldCheck, title: "Transparent Rules", body: "No hidden conditions. No gimmicks. Hit your target, follow the rules, get funded." },
              { icon: Zap, title: "Instant Assignment", body: "Skip evaluation hassle and get access to live funding instantly" },
              { icon: Users, title: "Elite Community", body: "Discord access, weekly market briefs, monthly competitions with prize pools. coming soon.." },
            ].map(({ icon: Icon, title, body }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="glass rounded-xl p-6 hover:border-[#ff7a00]/40 transition-all"
                data-testid={`why-card-${i}`}
              >
                <div className="w-11 h-11 rounded-lg bg-[#ff7a00]/10 border border-[#ff7a00]/30 flex items-center justify-center text-[#ff7a00] mb-4">
                  <Icon size={20} />
                </div>
                <div className="font-display text-lg font-semibold">{title}</div>
                <div className="text-[#8a8a8a] text-sm mt-2 leading-relaxed">{body}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-[#ff7a00] text-xs tracking-[0.3em] uppercase mb-3">The Process</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold max-w-2xl">Four steps to funded capital.</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {[
              { n: "01", title: "Pick a Challenge", body: "Choose your account size and challenge type. Pay in crypto, instantly." },
              { n: "02", title: "Get Credentials", body: "Get trading account details " },
              { n: "03", title: "Trade & Pass", body: "Hit your profit target while respecting drawdown limits. Show discipline." },
              { n: "04", title: "Get Funded & Paid", body: "Receive funded credentials. Trade live. Request payouts." },
            ].map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="relative glass rounded-xl p-6"
                data-testid={`step-card-${s.n}`}
              >
                <div className="absolute -top-4 -left-2 font-display text-7xl font-bold text-[#ff7a00]/15 leading-none">{s.n}</div>
                <div className="relative font-display text-lg font-semibold mt-6">{s.title}</div>
                <div className="text-[#8a8a8a] text-sm mt-3 leading-relaxed">{s.body}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 border-t border-[#ff7a00]/10 bg-[#080808]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-[#ff7a00] text-xs tracking-[0.3em] uppercase mb-3">Voices from the Pack</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold max-w-2xl">Funded traders. Real stories.</h2>
          <div className="grid md:grid-cols-3 gap-5 mt-12">
            {[
              { name: "Kwame J.", country: "Ghana", quote: "Passed in 11 days. Got funded $50K. The review process was quick and smooth", size: "$50,000" },
              { name: "Priya S.", country: "India", quote: "I don't worry about bank issue as I get paid in crypto", size: "$25,000" },
              { name: "Pedro A.", country: "Brazil", quote: "Synthetic indices 24/7, generous drawdown, fair rules. Best prop I've used.", size: "$100,000" },
            ].map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="glass rounded-xl p-6"
                data-testid={`testimonial-${i}`}
              >
                <div className="text-[#ff7a00] text-3xl font-display leading-none">"</div>
                <p className="text-[#cfcfcf] text-sm mt-2 leading-relaxed">{t.quote}</p>
                <div className="mt-5 pt-4 border-t border-[#ff7a00]/15 flex items-center justify-between">
                  <div>
                    <div className="text-[#f5f5f5] font-semibold text-sm">{t.name}</div>
                    <div className="text-[#8a8a8a] text-xs">{t.country}</div>
                  </div>
                  <div className="text-[#ff9a2f] font-display font-bold">{t.size}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative overflow-hidden">
        <Embers count={14} />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 text-center relative">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold">Ready to hunt?</h2>
          <p className="text-[#8a8a8a] mt-4 max-w-xl mx-auto">Join the pack. Prove your edge. Trade live capital.</p>
          <Link to="/challenges" data-testid="cta-bottom">
            <Button size="lg" className="mt-8 bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505] font-semibold h-12 px-8 glow-orange">
              View Challenges <ArrowRight size={18} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
